import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import butterflyApi from '../ButterflyApi'
import MobilePage from '../UiComponents/MobilePage'
import { isLogin } from '../Utils/Cookies'
import { callName } from '../Utils/Strings'
import { getAppProductionStatusText, hasPaidAppProduction, replaceAppTitleCallName } from './appProductionStatus'
import './FairytaleCatalog.css'

const CATEGORY_TITLE = {
  fairytale: '동화 제작 신청',
  game: '게임 제작 신청',
}

const CATEGORY_DESCRIPTION = {
  fairytale: '동화 제작 신청 시, 신청 순서대로 동화가 제작됩니다.',
  game: '구매 가능한 게임 제작 신청 항목입니다.',
}

const PRODUCT_THEMES = [
  { name: 'purple', icon: '★' },
  { name: 'mint', icon: '♥' },
  { name: 'yellow', icon: '☾' },
  { name: 'blue', icon: '☁' },
]

const BOOKS_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/image/purchase/books.png`

const normalizeApps = (response) => {
  if (!response) return []
  if (Array.isArray(response)) return response
  return response.items || []
}

const formatPrice = (price) => {
  if (price === undefined || price === null) return ''
  return `${new Intl.NumberFormat('ko-KR').format(price)}원`
}

const getPurchasePrice = (app) => {
  if (!app || app.salePrice === undefined || app.salePrice === null) return
  return Math.max(app.salePrice - (app.discountPrice || 0), 0)
}

const replaceCallName = (text, heroCallName) => {
  return (text || '').replace(/\{callName\}/g, heroCallName)
}

const getLocalHeroes = () => {
  try {
    return JSON.parse(localStorage.getItem('heroes')) || []
  } catch (e) {
    return []
  }
}

const getHeroCallName = () => {
  if (!isLogin()) return '하늘이'

  const heroNo = localStorage.getItem('heroNo')
  const heroes = getLocalHeroes()
  const hero = heroes.find(hero => `${hero.heroNo}` === `${heroNo}`)

  return hero ? callName(hero.firstName) : '하늘이'
}

const getSelectedHeroNo = () => {
  return isLogin() ? localStorage.getItem('heroNo') : ''
}

const getProductTheme = (index) => {
  return PRODUCT_THEMES[index % PRODUCT_THEMES.length]
}

const ProductThumbnail = ({ app, appName, theme, useBookSprite }) => {
  const bookSpriteStyle = { backgroundImage: `url(${BOOKS_IMAGE_URL})` }

  return <div className={`fairytale-product-thumb fairytale-product-thumb--${theme.name}`}>
    {
      useBookSprite ? <span
        className={`fairytale-book-sprite fairytale-book-sprite--${theme.name}`}
        style={bookSpriteStyle}
        role='img'
        aria-label={appName}
      /> : app.imageUrl ? <img
        className='fairytale-product-image'
        src={app.imageUrl}
        alt={appName}
      /> : <span
        className={`fairytale-book-sprite fairytale-book-sprite--${theme.name}`}
        style={bookSpriteStyle}
        role='img'
        aria-label={appName}
      />
    }
  </div>
}

const AppList = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category') || ''
  const [apps, setApps] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const heroCallName = useMemo(() => getHeroCallName(), [])
  const title = CATEGORY_TITLE[category] || '신청 항목'
  const description = CATEGORY_DESCRIPTION[category] || '구매 가능한 신청 항목을 확인해 보세요.'
  const isFairytaleCategory = category === 'fairytale'
  const pageClassName = isFairytaleCategory ? 'storybook-page fairytale-catalog' : undefined
  const ProductTitle = isFairytaleCategory ? 'h3' : 'h2'

  const handleApiError = useCallback((error) => {
    const message = (error.response && error.response.data && error.response.data.message) || error.message
    setErrorMessage(message || '신청 항목을 불러오지 못했습니다.')
  }, [])

  const loadApps = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')

    const response = await butterflyApi.getApps(category, getSelectedHeroNo(), handleApiError)
    setApps(normalizeApps(response))
    setLoading(false)
  }, [category, handleApiError])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    loadApps()
  }, [loadApps])

  if (loading) {
    return <MobilePage className={pageClassName} loading loadingMessage='신청 항목을 불러오고 있습니다.' />
  }

  return <MobilePage className={pageClassName}>
    <div className='fairytale-app-page'>
      <main className='fairytale-app-content'>
        {isFairytaleCategory ? <header className='catalog-intro'>
          <p className='book-eyebrow'><span aria-hidden='true'>✦</span> 우리 아이를 위한 특별한 한 권</p>
          <div className='catalog-title-row'>
            <h1 className='book-title'>{title}</h1>
            <img src='/image/main/books.png' alt='' width='60' height='60' />
          </div>
          <p className='book-description'>아이의 이름과 모습이 담긴 동화.<br />함께 떠날 다음 모험을 만들어 주세요.</p>
          <Link className='book-text-link' to='/fairytales?sample=true'>샘플 동화 먼저 둘러보기 <span aria-hidden='true'>↗</span></Link>
        </header> : <header className='fairytale-page-header'>
          <div className='fairytale-hero-copy'>
            <h1 className='fairytale-page-title'>{title}</h1>
            <p className='fairytale-page-description'>{description}</p>
          </div>
        </header>}

        {
          errorMessage && <div className='fairytale-message fairytale-message--error' role='alert'>
            {errorMessage}
          </div>
        }

        {
          !errorMessage && apps.length === 0 && <div className='fairytale-empty-card'>
            {isFairytaleCategory ? <>
              <span className='catalog-empty-icon' aria-hidden='true'>✧</span>
              <h2>새로운 이야기를 준비하고 있어요</h2>
              <p>지금은 신청할 수 있는 동화가 없어요.<br />기다리는 동안 이야기 책장을 둘러보세요.</p>
              <Link className='book-secondary-button' to='/fairytales'>이야기 책장으로 <span aria-hidden='true'>→</span></Link>
            </> : <>
              <div>구매 가능한 신청 항목이 없습니다.</div>
              <button
                type='button'
                onClick={() => navigate('/')}
              >
                홈으로 이동
              </button>
            </>}
          </div>
        }

        {
          !errorMessage && apps.length > 0 && <section
            className='fairytale-product-list'
            aria-label={isFairytaleCategory ? '제작 가능한 동화' : title}
          >
            {
              apps.map((app, index) => {
                const theme = getProductTheme(index)
                const appName = replaceAppTitleCallName(app.title || app.appName, app, heroCallName)
                const appDescription = replaceCallName(app.description, heroCallName)
                const purchasePrice = getPurchasePrice(app)
                const productionStatusText = getAppProductionStatusText(app.productionStatus)
                const canRunApp = Boolean(app.resultUrl)
                const paymentLocked = hasPaidAppProduction(app) && !canRunApp
                const actionLabel = canRunApp
                  ? (isFairytaleCategory ? '완성된 동화 보기' : '실행하기')
                  : paymentLocked ? '주문내역' : '신청하기'

                return <article
                  key={app.appNo}
                  className={`fairytale-product-card fairytale-product-card--${theme.name}`}
                >

                  <ProductThumbnail
                    app={app}
                    appName={appName}
                    theme={theme}
                    useBookSprite={isFairytaleCategory}
                  />

                  <div className='fairytale-product-body'>
                    <ProductTitle>{appName}</ProductTitle>
                    {
                      productionStatusText && <div className={`fairytale-production-status${app.productionStatus === 'CANCELED' ? ' catalog-status--canceled' : ''}`}>
                        {productionStatusText}
                      </div>
                    }
                    {
                      appDescription && <div className='fairytale-product-description'>
                        <span aria-hidden='true'>{theme.icon}</span>
                        <p>{appDescription}</p>
                      </div>
                    }

                    {!isFairytaleCategory && <div className='fairytale-price-box'>
                      <div>
                        <span>판매가</span>
                        <strong className='fairytale-sale-price'>{formatPrice(app.salePrice)}</strong>
                      </div>
                      <div>
                        <span>할인</span>
                        <strong className='fairytale-discount-price'>-{formatPrice(app.discountPrice || 0)}</strong>
                      </div>
                      <div>
                        <span>구매가</span>
                        <strong className='fairytale-purchase-price'>{formatPrice(purchasePrice)}</strong>
                      </div>
                    </div>}
                  </div>

                  {isFairytaleCategory && <dl className='catalog-price'>
                    {app.discountPrice > 0 && <>
                      <div className='catalog-price-original'>
                        <dt>판매가</dt>
                        <dd><del>{formatPrice(app.salePrice)}</del></dd>
                      </div>
                      <div className='catalog-price-discount'>
                        <dt>할인</dt>
                        <dd>−{formatPrice(app.discountPrice)}</dd>
                      </div>
                    </>}
                    <div className='catalog-price-total'>
                      <dt>구매가</dt>
                      <dd><strong>{formatPrice(purchasePrice)}</strong></dd>
                    </div>
                  </dl>}

                  <button
                    type='button'
                    className={isFairytaleCategory
                      ? `${canRunApp || paymentLocked ? 'book-secondary-button' : 'book-primary-button'} catalog-request`
                      : 'fairytale-request-button'}
                    aria-label={`${appName} ${actionLabel}`}
                    onClick={() => {
                      if (canRunApp) {
                        window.location.href = app.resultUrl
                        return
                      }

                      if (paymentLocked) {
                        navigate('/orders')
                        return
                      }

                      navigate(`/apps/${app.appNo}/payment`)
                    }}
                  >
                    {actionLabel}
                    <span aria-hidden='true'>{isFairytaleCategory ? '→' : '›'}</span>
                  </button>
                </article>
              })
            }
          </section>
        }

        {
          isFairytaleCategory && <>
            <aside className='catalog-guide' aria-labelledby='catalog-guide-title'>
              <span className='book-eyebrow'><span aria-hidden='true'>✧</span> 한 권의 동화가 완성되기까지</span>
              <h2 id='catalog-guide-title'>우리 아이의 이야기를 정성껏 만들어요</h2>
              <p>동화는 신청 순서대로 제작돼요.<br />제작 진행 상황은 주문내역에서 확인할 수 있어요.</p>
              <Link className='book-text-link' to='/orders'>주문내역 확인하기 <span aria-hidden='true'>↗</span></Link>
            </aside>
            <p className='catalog-footnote'><span aria-hidden='true'>✦</span> 오늘의 선물이, 오래 간직할 이야기가 되도록.</p>
          </>
        }
      </main>
    </div>
  </MobilePage>
}

export default AppList
